import React, { useState, useRef } from 'react';
import { DocumentArrowUpIcon, TrashIcon, EyeIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';
import apiService from '../../services/api';

interface Document {
    id: number;
    type: string;
    typeDescription: string;
    fileName: string;
    fileSize: number;
    contentType: string;
    status: string;
    uploadedAt: string;
    rejectionReason?: string;
    isDeleted?: boolean;
}

interface DocumentUploadProps {
    documentType: string;
    typeDescription: string;
    isRequired: boolean;
    onUploadSuccess?: (document: Document) => void;
    onDeleteSuccess?: (documentId: number) => void;
    existingDocument?: Document;
    disabled?: boolean;
}

export const DocumentUpload: React.FC<DocumentUploadProps> = ({
    documentType,
    typeDescription,
    isRequired,
    onUploadSuccess,
    onDeleteSuccess,
    existingDocument,
    disabled = false
}) => {
    const [isUploading, setIsUploading] = useState(false);
    const [document, setDocument] = useState<Document | undefined>(existingDocument);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (file.type !== 'application/pdf') {
            toast.error('Разрешены только PDF файлы');
            return;
        }

        // Validate file size (50MB max)
        const maxSize = 50 * 1024 * 1024;
        if (file.size > maxSize) {
            toast.error('Размер файла не должен превышать 50MB');
            return;
        }

        setIsUploading(true);

        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('type', documentType);

            const response = await apiService.uploadDocument(formData);

            if (response.data.success) {
                const uploadedDoc = (response.data as any).document;
                setDocument(uploadedDoc);
                onUploadSuccess?.(uploadedDoc);
                toast.success('Документ успешно загружен');
            } else {
                toast.error(response.data.message);
            }
        } catch (error: any) {
            console.error('Ошибка загрузки документа:', error);
            const message = error.response?.data?.message || 'Ошибка загрузки документа';
            toast.error(message);
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleDelete = async () => {
        if (!document) return;

        // eslint-disable-next-line no-restricted-globals
        if (!confirm('Вы уверены, что хотите удалить этот документ? Документ будет полностью удален и его нельзя будет восстановить.')) return;

        try {
            const response = await apiService.deleteDocument(document.id);

            if (response.data.success) {
                // Помечаем документ как удаленный локально
                setDocument({ ...document, isDeleted: true });
                onDeleteSuccess?.(document.id);
                toast.success('Документ успешно удален');

                // Через небольшую задержку полностью убираем из состояния
                setTimeout(() => {
                    setDocument(undefined);
                }, 1000);
            } else {
                toast.error(response.data.message || 'Ошибка удаления документа');
            }
        } catch (error: any) {
            console.error('Ошибка удаления документа:', error);
            const message = error.response?.data?.message || 'Ошибка удаления документа';
            toast.error(message);
        }
    };

    const handleView = async () => {
        if (!document) return;

        try {
            const response = await apiService.downloadDocument(document.id);
            if (response.data.success) {
                if (response.data.isBlob) {
                    // Для файлов из S3 открываем blob URL
                    window.open(response.data.url, '_blank');
                } else {
                    // Для локальных файлов открываем обычный URL
                    window.open(response.data.url, '_blank');
                }
            } else {
                toast.error(response.data.message || 'Ошибка открытия документа');
            }
        } catch (error: any) {
            console.error('Ошибка открытия документа:', error);
            const message = error.response?.data?.message || 'Ошибка открытия документа';
            toast.error(message);
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'APPROVED':
                return 'text-green-600';
            case 'REJECTED':
                return 'text-red-600';
            case 'NEEDS_REVISION':
                return 'text-yellow-600';
            case 'PENDING_MODERATION':
                return 'text-blue-600';
            default:
                return 'text-gray-600';
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'APPROVED':
                return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
            case 'REJECTED':
            case 'NEEDS_REVISION':
                return <ExclamationCircleIcon className="h-5 w-5 text-red-500" />;
            case 'PENDING_MODERATION':
                return <ExclamationCircleIcon className="h-5 w-5 text-blue-500" />;
            default:
                return null;
        }
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
        <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                    <h3 className="font-medium text-gray-900">{typeDescription}</h3>
                    {isRequired && <span className="text-red-500 text-sm">*</span>}
                </div>
                {document && (
                    <div className="flex items-center space-x-2">
                        {getStatusIcon(document.status)}
                        <span className={`text-sm font-medium ${getStatusColor(document.status)}`}>
                            {document.status === 'APPROVED' && 'Одобрен'}
                            {document.status === 'REJECTED' && 'Отклонен'}
                            {document.status === 'NEEDS_REVISION' && 'Требует доработки'}
                            {document.status === 'PENDING_MODERATION' && 'На модерации'}
                        </span>
                    </div>
                )}
            </div>

            {!document ? (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                    <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="mt-4">
                        <button
                            type="button"
                            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={isUploading || disabled}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            {isUploading ? 'Загрузка...' : 'Выбрать файл'}
                        </button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf"
                            className="hidden"
                            onChange={handleFileSelect}
                            disabled={disabled}
                        />
                    </div>
                    <p className="mt-2 text-sm text-gray-500">
                        Только PDF файлы, максимум 50MB
                    </p>
                </div>
            ) : (
                <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                                {document.fileName}
                            </p>
                            <p className="text-sm text-gray-500">
                                {formatFileSize(document.fileSize)} • Загружен {new Date(document.uploadedAt).toLocaleString('ru-RU')}
                            </p>
                            {document.rejectionReason && (
                                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                                    <p className="text-sm text-red-700">
                                        <strong>Причина отклонения:</strong> {document.rejectionReason}
                                    </p>
                                </div>
                            )}
                        </div>
                        <div className="flex items-center space-x-2 ml-4">
                            <button
                                type="button"
                                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                                onClick={handleView}
                                title="Просмотреть документ"
                            >
                                <EyeIcon className="h-5 w-5" />
                            </button>
                            {!disabled && document.status !== 'APPROVED' && (
                                <>
                                    <button
                                        type="button"
                                        className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                                        onClick={handleDelete}
                                        title="Удалить документ"
                                    >
                                        <TrashIcon className="h-5 w-5" />
                                    </button>
                                    <button
                                        type="button"
                                        className="btn-secondary text-sm"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={isUploading}
                                    >
                                        {isUploading ? 'Загрузка...' : 'Заменить'}
                                    </button>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".pdf"
                                        className="hidden"
                                        onChange={handleFileSelect}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
